/** "My Account" dialog for a logged-in user: profile summary, verify-email
 * resend, and self-service API key rotation (for the desktop viewer /
 * scripted access) -- no admin key needed, unlike routes_admin.py's version
 * of key rotation. */

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AccountProfile } from '../lib/types';

export function AccountDialog({
  onClose,
  onAccountChange,
}: {
  onClose: () => void;
  onAccountChange: () => void | Promise<void>;
}) {
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setAccount(await api.getAccount());
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Could not load your account.');
      }
    })();
  }, []);

  async function rotateKey() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.rotateApiKey();
      setNewKey(result.api_key);
      // The desktop viewer/scripted callers read this from localStorage;
      // updating it here means "Copy key" and the running web session both
      // reflect the same, current key immediately.
      api.setApiKey(result.api_key);
      setAccount(result);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not rotate your API key.');
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.resendVerification();
      setVerifyMessage(result.message);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not resend verification email.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="My account">
      <div className="card">
        <div className="card-head">
          <h2>My account</h2>
        </div>
        <div className="card-body stack">
          {!account && !error && <p className="hint">Loading…</p>}
          {error && (
            <div className="hint" style={{ color: 'var(--fail-600, #c0392b)' }}>
              {error}
            </div>
          )}

          {account && (
            <>
              <div>
                <div className="hint-strong">{account.name || account.email}</div>
                <div className="hint">{account.email}</div>
              </div>

              {!account.email_verified && (
                <div className="callout callout-warn">
                  <div>
                    Your email isn't verified yet.{' '}
                    <button
                      type="button"
                      className="btn-link"
                      onClick={resendVerification}
                      disabled={busy}
                    >
                      Resend verification email
                    </button>
                  </div>
                </div>
              )}
              {verifyMessage && <div className="hint">{verifyMessage}</div>}

              <div>
                <div className="hint-strong">API key</div>
                <div className="hint">
                  For the desktop viewer or scripted access. Current key starts with{' '}
                  <code>{account.key_prefix || '—'}</code>.
                </div>
                {newKey ? (
                  <div className="stack" style={{ gap: '0.3rem', marginTop: '0.4rem' }}>
                    <input type="text" readOnly value={newKey} onFocus={(e) => e.target.select()} />
                    <div className="hint">
                      Copy this now -- it won't be shown again. Your previous key stopped working.
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ marginTop: '0.4rem' }}
                    onClick={rotateKey}
                    disabled={busy}
                  >
                    {account.key_prefix ? 'Rotate API key' : 'Create an API key'}
                  </button>
                )}
              </div>
            </>
          )}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-sm btn-accent"
              onClick={async () => {
                onClose();
                await onAccountChange();
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
